import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, Receipt, Percent, CalendarDays } from 'lucide-react';

import { CollapsibleSection } from '../../components/ui/CollapsibleSection';
import { reportError } from '../../lib/errors';
import toast from 'react-hot-toast';
import { useVertical } from '../../context/VerticalContext';
import { useFinancialData } from '../../hooks/useFinancialData';
import { useRole } from '../../context/RoleContext';

interface TaxConfig {
    tax_region: 'rUK' | 'scotland';
    is_vat_registered: boolean;
    vat_registered_from: string | null;
    vat_registration_number?: string | null;
}

const DEFAULT_CONFIG: TaxConfig = {
    tax_region: 'rUK',
    is_vat_registered: false,
    vat_registered_from: null,
    vat_registration_number: null
};

export const FiscalSettingsSection = () => {
    const { activeOrgId } = useVertical();
    const { refresh } = useFinancialData();
    const { isOwner } = useRole();
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<TaxConfig>(DEFAULT_CONFIG);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [orgSettings, setOrgSettings] = useState<any>({});

    useEffect(() => {
        let cancelled = false;
        const fetchConfig = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (cancelled) return;
            setLoading(true);
            setOrgId(null);
            setOrgSettings({});
            setConfig(DEFAULT_CONFIG);
            if (!user) { if (!cancelled) setLoading(false); return; }

            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'fiscal-settings-section.profiles');

            if (cancelled) return;
            if (!profile?.active_org_id) {
                setLoading(false);
                return;
            }

            setOrgId(profile.active_org_id);

            // Get VAT settings from the org's settings JSON
            const { data: orgData, error: orgDataError } = await supabase
                .from('organizations')
                .select('settings')
                .eq('id', profile.active_org_id)
                .single();
            if (orgDataError) reportError(orgDataError, 'fiscal-settings-section.organizations');

            if (cancelled) return;
            if (orgDataError) { setOrgId(null); setLoading(false); return; }
            if (orgData?.settings) {
                setOrgSettings(orgData.settings);
                setConfig({
                    tax_region: orgData.settings.tax_region === 'scotland' ? 'scotland' : 'rUK',
                    is_vat_registered: orgData.settings.is_vat_registered ?? false,
                    vat_registered_from: orgData.settings.vat_registered_from ?? null,
                    vat_registration_number: orgData.settings.vat_registration_number ?? null
                });
            }

            setLoading(false);
        };

        void fetchConfig();
        return () => { cancelled = true; };
    }, [activeOrgId]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setSaved(false);
        if (!orgId || orgId !== activeOrgId || !isOwner) { setSaving(false); return; }

        // Validate: VAT registration requires an effective date
        if (config.is_vat_registered && !config.vat_registered_from) {
            setSaving(false);
            toast.error('Please enter the date you became VAT registered. This determines which jobs include VAT.');
            return;
        }

        const updatedSettings = {
            ...orgSettings,
            tax_region: config.tax_region,
            is_vat_registered: config.is_vat_registered,
            vat_registered_from: config.is_vat_registered ? config.vat_registered_from : null,
            vat_registration_number: config.is_vat_registered ? config.vat_registration_number : null
        };

        const { error } = await supabase
            .from('organizations')
            .update({ settings: updatedSettings })
            .eq('id', orgId)
            .eq('settings', JSON.stringify(orgSettings))
            .select('id')
            .single();

        if (error) {
            setSaving(false);
            toast.error('Tax & VAT settings could not be saved. Reload the page and try again.');
            reportError(error, 'fiscal-settings-section.save');
            return;
        }

        setOrgSettings(updatedSettings);
        await refresh();
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    }, [orgId, activeOrgId, isOwner, orgSettings, config, refresh]);

    if (loading || (orgId !== null && orgId !== activeOrgId)) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-accent" />
                    Tax &amp; VAT
                </h3>
                <p className="text-sm text-fg-2">VAT registration and tax configuration</p>
            </div>

            <fieldset disabled={!isOwner || !orgId || saving} className="space-y-2 disabled:opacity-70">
                <label className="block p-3 text-sm text-fg">
                    Income tax region
                    <select value={config.tax_region} onChange={e => setConfig({ ...config, tax_region: e.target.value as TaxConfig['tax_region'] })} className="mt-2 w-full bg-panel border border-line rounded-[var(--fds-radius)] px-3 py-2 text-sm">
                        <option value="rUK">England, Wales &amp; Northern Ireland</option>
                        <option value="scotland">Scotland</option>
                    </select>
                    <span className="mt-2 block text-xs text-fg-2">Choose the sole trader's income tax region based on where they live. Class 4 NI is the same across the UK.</span>
                </label>
                <CollapsibleSection title="VAT Registration" icon={<Percent className="w-4 h-4" />} defaultOpen={true}>

                    {/* VAT Registered Toggle */}
                    <label className="flex items-center justify-between p-3 bg-raised border border-line rounded-[var(--fds-radius)] cursor-pointer hover:bg-raised transition-colors">
                        <div className="flex items-center gap-3">
                            <Percent className="w-4 h-4 text-fg-2" />
                            <div>
                                <p className="text-sm font-medium text-fg">VAT Registered</p>
                                <p className="text-xs text-fg-2">Enable if your business is VAT registered</p>
                            </div>
                        </div>
                        <div className="relative">
                            <input
                                type="checkbox"
                                checked={config.is_vat_registered}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setConfig({
                                        ...config,
                                        is_vat_registered: checked,
                                        vat_registered_from: checked ? config.vat_registered_from : null,
                                        vat_registration_number: checked ? config.vat_registration_number : null
                                    });
                                }}
                                className="sr-only fd-switch-input"
                            />
                            <span className="fd-switch" aria-hidden="true" />
                        </div>
                    </label>

                    {/* VAT Date & Number — shown when VAT is on */}
                    {config.is_vat_registered && (
                        <div className="flex flex-col gap-3 px-3 py-3 bg-raised border border-t-0 border-line rounded-b-[var(--fds-radius)] animate-in slide-in-from-top-1 duration-200">
                            <div className="flex items-center gap-3">
                                <CalendarDays className="w-4 h-4 text-fg-2 shrink-0" />
                                <label className="text-xs text-fg-2 whitespace-nowrap w-28">
                                    Registered from <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={config.vat_registered_from || ''}
                                    onChange={(e) => setConfig({ ...config, vat_registered_from: e.target.value || null })}
                                    required
                                    className={`flex-1 bg-panel border rounded-[var(--fds-radius)] px-3 py-1.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all ${!config.vat_registered_from ? 'border-danger/50' : 'border-line'}`}
                                />
                                {!config.vat_registered_from && (
                                    <span className="text-[10px] text-danger whitespace-nowrap">Required</span>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <Receipt className="w-4 h-4 text-fg-2 shrink-0" />
                                <label className="text-xs text-fg-2 whitespace-nowrap w-28">VAT Number</label>
                                <input
                                    type="text"
                                    placeholder="GB 123 4567 89"
                                    value={config.vat_registration_number || ''}
                                    onChange={(e) => setConfig({ ...config, vat_registration_number: e.target.value || null })}
                                    className="flex-1 bg-panel border border-line rounded-[var(--fds-radius)] px-3 py-1.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all uppercase placeholder:normal-case"
                                />
                            </div>
                        </div>
                    )}

                </CollapsibleSection>

                <div className="pt-4">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 fd-btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold rounded-[var(--fds-radius)] transition-all"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? '✓ Saved' : 'Save Changes'}
                    </button>
                </div>
            </fieldset>
        </div>
    );
};
